/**
 * Arable field types and API client.
 *
 * This file contains only type definitions and the API client.
 */

import type { Feature, FeatureCollection } from "geojson";
import { multiPolygonToPolygon, polygonToMultiPolygon } from "common/utils";
import { ARABLE_TYPE, FIELD_TYPE } from "model/commonTypes";

type Visibility = "none" | "visible";

export type ArableFieldResponse = {
  guid: string;
  name: string;
  farm: string;
  geom: FeatureCollection;
  area: number;
  tiles?: Array<string>;
  arableType: ARABLE_TYPE;
};

export type ArableField = {
  guid: string;
  name: string;
  farm: string;
  geom: Feature;
  area: number;
  invalidatedTimestamp?: number;
  tiles?: Array<string>;
  visibility?: Visibility;
  arableType: ARABLE_TYPE;
};

export type ArableFieldUpdate = {
  guid: string;
  name?: string;
  farm?: string;
  geom?: Feature;
  area?: number;
  invalidatedTimestamp?: number;
  visibility?: Visibility;
};

/**
 * Prepare an arable field for API request.
 */
export const prepareArableFieldRequest = (field: ArableField) => {
  return {
    ...field,
    fieldType: FIELD_TYPE.ARABLE,
    geom: {
      type: "FeatureCollection",
      features: field.geom && [polygonToMultiPolygon(field.geom)],
    } as FeatureCollection,
  };
};

/**
 * Process arable field responses from the API.
 */
export const processArableFieldResponse = (
  fields: Array<ArableFieldResponse>
) => {
  return fields.map((field: ArableFieldResponse) => {
    // Defensive: an already-processed ArableField (geom is a Polygon Feature with no
    // `features` array) can reach here from the persisted cache on a cold start.
    // Re-processing it would read `geom.features[0]` of undefined.
    if (!(field.geom as { features?: unknown })?.features) {
      return field as unknown as ArableField;
    }
    const area = field.geom.features[0].properties?.area;
    const geom = multiPolygonToPolygon(
      field.geom.features[0],
      field.guid
    ) as Feature;
    const f: ArableField = {
      ...field,
      visibility: "visible",
      area: area,
      geom,
    };
    return f;
  });
};
